/**
 * The deterministic stat tiles for the tracker home. Every number here is
 * pre-computed server-side (facts.session_stats) — this just renders.
 *
 * Honesty by design: the sample-size caveat is shown prominently, and we
 * never imply a small-sample win rate is predictive.
 */

import type { SessionStats } from "@/shared/models";
import {
  bbPerHour,
  confidenceLabel,
  hoursLabel,
  money,
  netToneClass,
} from "@/features/tracker/lib/trackerFormat";

function Tile({
  label,
  value,
  tone,
  sub,
}: {
  label: string;
  value: string;
  tone?: string;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-bg px-3 py-2.5">
      <div className="text-[11px] uppercase tracking-wider text-text-muted">
        {label}
      </div>
      <div className={`mt-0.5 text-lg font-semibold tabular-nums ${tone ?? "text-text"}`}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-text-muted">{sub}</div>}
    </div>
  );
}

export function StatCards({ stats }: { stats: SessionStats }) {
  const rate =
    stats.bbPerHour !== null
      ? bbPerHour(stats.bbPerHour)
      : money(stats.netPerHour) + "/hr";

  const streakLabel =
    stats.currentStreak === 0
      ? "—"
      : `${Math.abs(stats.currentStreak)} ${stats.currentStreak > 0 ? "W" : "L"}`;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <Tile label="Net" value={money(stats.netTotal)} tone={netToneClass(stats.netTotal)} />
        <Tile label="Hours" value={hoursLabel(stats.totalHours)} sub={`${stats.sessionCount} sessions`} />
        <Tile
          label="Rate"
          value={rate}
          tone={netToneClass(stats.bbPerHour ?? stats.netPerHour)}
        />
        <Tile label="Win rate" value={`${Math.round(stats.winRatePct)}%`} sub="of sessions up" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <Tile label="Best" value={money(stats.biggestWin)} tone="text-accent" />
        <Tile label="Worst" value={money(stats.biggestLoss)} tone="text-danger" />
        <Tile
          label="Streak"
          value={streakLabel}
          tone={stats.currentStreak > 0 ? "text-accent" : stats.currentStreak < 0 ? "text-danger" : undefined}
        />
        <Tile label="Last 10" value={money(stats.formNet)} tone={netToneClass(stats.formNet)} />
      </div>

      {/* Honesty: deterministic sample-size framing. Never sell ROI proof. */}
      <div className="rounded-xl border border-border/70 bg-bg px-3 py-2.5 text-xs text-text-muted leading-relaxed">
        <span className="font-medium text-text">{confidenceLabel(stats.confidence)}.</span>{" "}
        {stats.sampleCaveat}
      </div>
    </div>
  );
}
